import { each, map } from 'lodash';
import * as React from 'react';

import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import HelpIcon from '@mui/icons-material/Help';
import SecurityIcon from '@mui/icons-material/Security';
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { Badge, Box, Button, CircularProgress, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Location } from 'react-router-dom';
import IwButton from '../../components/app/IwButton';
import MaterialChatHistory from '../../components/chat/MaterialChatHistory';
import OverlayTitle from '../../components/generics/components/overlay-title';
import { withRouter } from '../../components/hoc/WithRouter';
import { APP_ELEMENT_DIMENSIONS } from '../../constants';
import { IChannel, ICohort, ITeam, IUser } from '../../stores/jotai/base';
import { isInPresentationMode } from '../../util';

interface Props {
    user?: IUser;
    team?: ITeam;
    org?: OrgState;
    cohort?: ICohort;
    appState?: AppState;
    pubState?: PubState;
    UIState?: any;
    baseAtomBridge: any;
    channels: IChannel[];
	loadChannelChat: (
		channelKey: string,
		onSuccess?: () => void,
		onFailure?: () => void
	) => void;
    theme?: Theme;
};

interface State {
    showChatHelpModal: boolean;
    newMessageBuffer: string;
    selectedChannel: string;
    selectedChannelIndex: number;
    chatHelpAnchor: any;
    loadingChat: boolean;
}

interface Refs {
    [key: string]: React.Component<any, any>;
    message: any;
}

interface ViewProps extends Props {
    canSplit?: boolean;
    channelIndex: number;
    isTop?: boolean;
    inPresentation?: boolean;
    presentationWeek: Number;
    presentationGroup: string;
    pubState: PubState;
    publishMessage?: any;
    org: OrgState;
    handleChatClose: any;
    isInstructorChat?: boolean;
    location?: Location
}

function channelIsInstructorOnly(channelID: string): boolean {
    if (channelID === undefined) return false;
    return channelID.endsWith('-IC');
}


class ChatViewClass extends React.Component<ViewProps, State> {
    refs: Refs;

    //@ts-ignore
    state: State = {
        showChatHelpModal: false,
        newMessageBuffer: '',
        chatHelpAnchor: null,
        selectedChannelIndex: 0,
        loadingChat: false,
    }

    shouldScrollToBottom: boolean = true;

    objectiveWeek: Number = 0;
    objectGroup: string = undefined;
    editCommentArea: any;

    chatListRef: React.RefObject<HTMLDivElement> = React.createRef();

    componentDidMount() {
        this.objectGroup = this.props?.presentationGroup;
        this.objectiveWeek = this.props?.presentationWeek;

        this.connectToChannel();
    }

	componentDidUpdate(prevProps) {
        this.scrollToBottom();
        
        const previousChannelKeys = prevProps?.channels?.map(
			(channel) => channel?.key
		);
		const currentChannelKeys = this?.props?.channels?.map(
			(channel) => channel?.key
		);

		if (
			previousChannelKeys.some((chKey) => !currentChannelKeys.includes(chKey))
		) {
			this.connectToChannel();
		}
	}

    connectToChannel() {
        const channel = this.getChannel();
        const channelID = channel?.key;

        // If the presentation flag is active store the new channel ID in our pubstate
        if (this.isInPresentation) {
            this.onSelectChannel(channel);
        }
        else {
            // When connecting to a channel start loading it's history and set it
            // as the base channel
            if (channelID) {
                this.onSelectChannel(channel);

                this.setState({
                    selectedChannel: channelID
                });
            }
        }
    }

    getIsChatHelpVisible = () => {
        const {
            showChatHelpModal
        } = this.state;

        return showChatHelpModal;
    }

    toggleIsChatHelpVisible = () => {
        this.setIsChatHelpVisible(this.state?.showChatHelpModal);
    }

    setIsChatHelpVisible = (value: boolean) => {
        this.setState({
            showChatHelpModal: value
        });
    }

    getChannels(props = this.props) {
        return props.channels;
    }

    getChannel(): {key: string, value: string} {
       if(this.props?.channels && this.props?.channels?.[this.state?.selectedChannelIndex]){
         return this.props?.channels?.[this.state?.selectedChannelIndex];
       }
    }

    scrollToBottom() {
        this.chatListRef.current.scroll(
            0,
            this.chatListRef.current?.scrollHeight
        );
    }

    onSelectChannel=(channel: IChannel) => {
        const {channels, loadChannelChat} = this.props;
        const channelIndex = channels?.findIndex((ch) => ch?.key === channel?.key);
            
        this.setState({ loadingChat: true });

        loadChannelChat(
            channel?.key,
            () => {
                this.setState({ loadingChat: false });
            },
            () => {
                this.setState({ loadingChat: false });
            }
        );

        this.setState({
            selectedChannelIndex: channelIndex,
        });
    }

    isLastMessageForUser(userID: string, messageID: string, history: any[]) {
        let wasThereAMatch = false;

        if (history?.length >= 1) {
            let lastMessageFromUser = undefined;

            for (let i = 0; i < history?.length; i++) {
                if (history[i]?.userID === userID || history[i]?.userID === undefined) {
                    lastMessageFromUser = history[i]?.id;
                }
            }

            if (messageID === lastMessageFromUser) {
                wasThereAMatch = true;
            }
        }

        return wasThereAMatch;
    }

    isSplitView = (): boolean => {
        return false;
    }

    isInPresentation = isInPresentationMode(this.props.location?.pathname);

    v3Scroll = () => {
        const ele = document.getElementsByClassName("channel-end-marker");

        const e = ele[0];

        if (e) {
            e.scrollIntoView();
        }
    }

    sendMessage = (event: any) => {
        // Don't allow sending messages in readonly mode
        if (this.getIsReadOnly()) return;

        const {
            channels
        } = this.props;
        const { selectedChannelIndex } = this.state;

        if (event.shiftKey) return;
        if (event.ctrlKey) return;

        let value = this.state.newMessageBuffer;

        if (value && value !== 'undefined') {
            const channel = channels?.[selectedChannelIndex];
            this.props.publishMessage(value, channel?.key);

            this.setState({
                newMessageBuffer: ''
            })

            this.shouldScrollToBottom = true;
        }
    }

    // Take a full channel name like Instructo General
    // and turn it into a 2 letter representation
    // If there are 2 or more words, take the first letter from the first 2
    // otherwise take just the first 2 letters
    convertChannelNameToBadge = (value: string) => {
        if (value === undefined) return '';

        const words = value.split(' ');

        if (words?.length === 1) {
            return value.slice(0, 2).toUpperCase();
        }

        return words[0]?.slice(0, 1).toUpperCase() + words[1]?.slice(0, 1).toUpperCase();
    }

    renderUnreadChatBadgeForChannel = (current: boolean, unread: any) => {
        // Check for "no" unread in any possible format for us
        if (unread === 0 || unread === undefined || unread === "0") {
            return <span></span>
        }

        return <Badge badgeContent={unread} color="error" sx={{ top: '-15px', left: 'calc(100% + 3px)',  '& .MuiBadge-badge': {
                color: 'white', 
                }, }} />
    }

    renderChatHelpPopover = () => {
        return <Popover
            // hasBackdrop={true}
            // backdropProps={{ className: 'new-feature-popover-backdrop' }}
            // enforceFocus={true}
            // canEscapeKeyClose={true}
            onClose={() => {
                // this.setIsChatHelpVisible(false)
                this.setState({ chatHelpAnchor: null })
            }}
            // isOpen={this.getIsChatHelpVisible()}
            // isOpen={this.state.showChatHelpModal}
            open={Boolean(this.state?.chatHelpAnchor)}
            anchorEl={this.state?.chatHelpAnchor}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
        >
            <div style={{ padding: '10px', maxWidth: '700px' }}>
                <h4 style={{ marginTop: '0px' }}>Chat Channels</h4>
                <p><strong>PF</strong> - Peer Feedback (Public): Per-team channel for teams to give feedback to a team.</p>
                <p><strong>TC</strong> - Team Chat (Team Only): Per-team channel for members of each team.</p>
                <p><strong>GC</strong> - General Chat (Public): Cohort-wide channel for all cohort members.</p>
                <p><strong>IF</strong> - Instructor Feedback (Instructor Only): Per-team channel for instructors and team members to give private feedback to a team.</p>
                <p><strong>IG</strong> - Instructor General  (Instructor Only): Cohort-wide channel for all instructors.</p>
                <p><strong>IC</strong> - Instructor Chat  (Instructor Only): Per-team private channel for instructors.</p>
                <h4>Posting Links In Chat</h4>
                <p>To create a link in Chat follow this format [title of link](link.url)</p>
                <p>[Innovation Within](innovationwithin.com)</p>
                <p>becomes: <a href="#">Innovation Within</a>  </p>
            </div>
        </Popover>
    }

    renderChannelSelect() {
        const { isTop, isInstructorChat } = this.props;
        const { theme } = this.props;
        if (isInstructorChat) {
            if (isTop) {
                return (
                    <Stack
                        direction="row"
                        sx={{ alignItems: "center", fontSize: "0.75rem" }}
                        spacing={1}
                    >
                        <Badge>
                            <SecurityIcon />
                        </Badge>
                        <Typography
                            component="span"
                        >
                            Private Instructor Chat
                        </Typography>
                    </Stack>
                );
            }
        }

        // We're going to render all of the options we have
        // accessible to us.  
        let channelTabs: JSX.Element[] = [];
        let channel = this.getChannel();

        if (channel !== undefined) {
            let items = map(this.getChannels(), channel => {
                let isInstructorChannel = channelIsInstructorOnly(channel?.key as string);

                //@ts-ignore
                let unread = this.props?.baseAtomBridge?.unread[channel?.key as string];

                if (!isInstructorChannel && (!unread)) return {
                    ...channel,
                    channel: channel?.value as string,
                    unread
                };

                let value = [
                    isInstructorChannel ?
                        null :
                        null,
                    channel.value
                ];

                let displayValue: Array<React.ReactElement | string>;
                if (unread) {
                    displayValue = value;

                    value = [...value, <Badge>
                        {unread}
                    </Badge>];
                }

                return {
                    key: channel.key,
                    value: value,
                    displayValue,
                    channel: channel?.value as string,
                    unread
                }
            });

            each(items, item => {
                // Mark which channel is selected
                const isSelected = item?.key === channel?.key;
                // const wrapperClasses = isSelected ? "iw-chat-button-wrapper iw-chat-button-wrapper-active" : "iw-chat-button-wrapper";

                channelTabs.push(
                    <Tooltip key={item?.key} title={item?.channel}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                const channel = {
                                    key: item?.key,
                                    value: item?.value[1],
                                };

                                this.setState({
                                    selectedChannel: item?.key,
                                });

                                this.onSelectChannel(channel);
                            }}
                            sx={{
                                marginRight: "6px",
                                height: "35px",
                                minWidth: isSelected ? "none" : "35px",
                                fontSize: "0.75rem",
                                color: isSelected
                                    ? theme?.custom?.mainButtonHighlight
                                    : theme?.custom?.mainIconColor,
                                borderColor: isSelected
                                    ? theme?.custom?.mainButtonHighlight
                                    : theme?.custom?.borderGrey,
                                ":hover": {
                                    color: theme?.custom?.mainButtonHighlight,
                                },
                            }}
                        >
                            {this.renderUnreadChatBadgeForChannel(
                                isSelected,
                                item?.unread
                            )}
                            {isSelected
                                ? item?.channel
                                : this.convertChannelNameToBadge(item?.channel)}
                        </Button>
                    </Tooltip>
                );
            });
        }

        return channelTabs;
    }

    renderChannelPlacard() {
        let selectedChannel = this.getChannel();
        const { theme } = this.props;
        if (selectedChannel) {
            let message: string;

            switch (selectedChannel?.value) {
                case 'Instructor Chat':
                    message = 'This channel is only visible to instructors to discuss a team.'
                    break;

                case 'Instructor General':
                    message = "This instructor only channel is for the instructors to discuss the cohort.";
                    break;

                case 'General Channel':
                    message = 'This channel is for everyone in this cohort to communicate.';
                    break;

                case 'Team Chat':
                    message = 'This channel is only visible to members of this team.';
                    break;

                case 'Peer Feedback':
                    message = 'This channel is for everyone in this cohort to provide presentation feedback for this team.';
                    break;

                case 'Instructor Feedback':
                    message = 'This channel is for your instructors and your team to discuss feedback.';
                    break;
            }

            if (message) return <Box sx={{
                margin: '8px',
                display: 'flex',
                justifyContent: 'center',
                border: `1px solid ${theme?.custom?.borderGrey}`,
                padding: '10px',
                alignItems: 'center',
                borderRadius: '4px',
                color: theme?.custom?.mainHeaderColor,
                // backgroundColor: '#fafafa'  
            }}>
                <Typography>{message}</Typography>
            </Box>
        }
    }

    split = () => {
        if (this.isInPresentation) return;
    }

    getIsReadOnly = () => {
        let selectedChannel = this.getChannel();

        if (selectedChannel) {
            if (selectedChannel?.value === 'Notifications') return true;
        }
        return false;
    }

    renderNewChatInput = () => {
        const { theme } = this.props;
        return (
            <Stack
                direction="row"
                sx={{
                    alignItems: "flex-end",
                    gap: "8px",
                }}
            >
                <TextareaAutosize
                    placeholder="Type your message"
                    value={this.state?.newMessageBuffer}
                    onChange={(e) => {
                        this.setState({
                            newMessageBuffer: e.target.value,
                        });
                    }}
                    style={{
                        fontSize: "14px",
                        lineHeight: 1.5,
                        resize: "none",
                        maxHeight: "160px",
                        overflow: "auto",
                        borderRadius: "4px",
                        borderColor: theme?.custom?.mainButtonHighlight,
                        outlineColor: theme?.custom?.mainButtonHighlight,
                        padding: "8px",
                    }}
                    className="iw-component-input-noresize"
                    ref={(ref) => (this.editCommentArea = ref)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey) {
                            e.preventDefault();

                            //@ts-ignore
                            this.sendMessage(e);

                            return false;
                        }
                        // if (e.key === "ArrowUp") {
                        //     if (this.state.newMessageBuffer === "") {
                        //         appActions?.setFlagUserEditLastMessage(1);
                        //     }
                        // }
                    }}
                />
                <IwButton
                    size="small"
                    sx={{
                        minWidth: "auto",
                    }}
                    onClick={(e) => {
                        this.sendMessage(e);
                    }}
                    disabled={!this.state.newMessageBuffer}
                >
                    <SendRoundedIcon />
                </IwButton>
            </Stack>
        );
    }

    render() {
        const { handleChatClose } = this.props;
        const { loadingChat } = this.state;
        const { theme } = this.props;

        let helpWidget = this.props.canSplit && <Tooltip title={"Learn more about chat"}>
            <IconButton sx={{ marginLeft: "auto" }} onClick={(e) => {
                this.setState({ chatHelpAnchor: e.currentTarget })
            }}>
                <HelpIcon />
            </IconButton>
        </Tooltip>;

        let newMessageWidget = this.renderNewChatInput();

        if (this.getChannels()?.length === 0) newMessageWidget = <span></span>

        return (
            <div
                className="iw-chat-region"
                style={{
                    display: "flex",
                    height: "100%",
                    flexDirection: "column",
                    maxHeight: "calc(100vh - 71px)",
                    width: APP_ELEMENT_DIMENSIONS.CHAT_PANEL.WIDTH,
                    minWidth: "300px",
                    maxWidth: "400px",
                }}
            >
                <OverlayTitle
                    sx={{
                        borderRadius: "0",
                        padding: "6px",
                        borderBottom: `1px solid ${theme.custom.borderGrey}`,
                        ".OverlayTitleText": {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        },
                        height: APP_ELEMENT_DIMENSIONS.CANVAS_TOOLBAR.HEIGHT,
                    }}
                    title={
                        <>
                            {this.renderChannelSelect()}
                            {this.renderChatHelpPopover()}
                            {helpWidget}
                        </>
                    }
                    onClose={handleChatClose}
                />
                <div
                    style={{
                        overflowY: "auto",
                        height: "100%",
                        scrollBehavior: "smooth",
                    }}
                    ref={this.chatListRef}
                >
                    {!loadingChat ? (
                        <Stack sx={{ overflow: "visible", height: "100%" }}>
                            {this.renderChannelPlacard()}
                            <MaterialChatHistory
                                channel={this.getChannel()}
                                chatHistory={
                                    this.props.baseAtomBridge?.history || []
                                }
                                unreadCount={
                                    this.props.baseAtomBridge?.unread?.[
                                        this.getChannel()?.key
                                    ]
                                }
                                app={this.props?.appState}
                                user={this.props?.user}
                                onScrollRequest={() => {
                                    this.chatListRef.current?.scroll(
                                        0,
                                        this.chatListRef.current?.scrollHeight
                                    );
                                }}
                                isLastMessageForUser={(
                                    messageID,
                                    sortedHistory
                                ) =>
                                    this.isLastMessageForUser(
                                        this.props?.user?.userID,
                                        messageID,
                                        sortedHistory
                                    )
                                }
                            />
                        </Stack>
                    ) : (
                        <Stack
                            sx={{
                                height: "100%",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress />
                        </Stack>
                    )}
                </div>
                <div
                    onClick={() => {
                        try {
                            this.editCommentArea.focus();
                        } catch (e) {}
                    }}
                    style={{
                        padding: "8px",
                        width: "100%",
                        borderTop: `1px solid ${theme?.custom?.borderGrey}`,
                    }}
                >
                    {newMessageWidget}
                </div>
            </div>
        );
    }
}

const ChatView = withRouter(ChatViewClass)
export { ChatView };
